import { useMemo, useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useSearchParams } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { accessService, AccessMemberRow, SurveyResponseRow } from '@/services/access.service';
import { Mail, RefreshCcw, Send, Eye, X, Trash2, Upload, RotateCcw, CheckCircle2, XCircle } from 'lucide-react';

export function AccessControlPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<'invited' | 'registered' | 'requests'>('invited');
  const [emailInput, setEmailInput] = useState('');

  const [showResponsesModal, setShowResponsesModal] = useState(false);
  const [responsesEmail, setResponsesEmail] = useState<string | null>(null);
  const [responses, setResponses] = useState<SurveyResponseRow[]>([]);

  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [revokeEmail, setRevokeEmail] = useState<string | null>(null);

  const [showImportModal, setShowImportModal] = useState(false);
  const [importJson, setImportJson] = useState('');

  const queryClient = useQueryClient();

  const status = activeTab === 'invited' ? 'invited' : 'registered';
  const { data: members = [], isLoading, refetch } = useQuery({
    queryKey: ['access-members', status],
    queryFn: () => accessService.listMembers(status),
  });

  // Get counts for both tabs
  const { data: invitedMembers = [] } = useQuery({
    queryKey: ['access-members', 'invited'],
    queryFn: () => accessService.listMembers('invited'),
  });

  const { data: registeredMembers = [] } = useQuery({
    queryKey: ['access-members', 'registered'],
    queryFn: () => accessService.listMembers('registered'),
  });

  // Get requests
  const { data: requests = [], isLoading: isLoadingRequests, refetch: refetchRequests } = useQuery({
    queryKey: ['access-requests'],
    queryFn: () => accessService.listRequests(),
  });

  const inviteMutation = useMutation({
    mutationFn: (email: string) => accessService.invite(email),
    onSuccess: async () => {
      toast.success('Invitation sent.', { duration: 4000 });
      setEmailInput('');
      await queryClient.invalidateQueries({ queryKey: ['access-members'] });
      await refetch();
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to send invitation.', { duration: 6000 });
    },
  });

  const resendMutation = useMutation({
    mutationFn: (email: string) => accessService.resend(email),
    onSuccess: async () => {
      toast.success('Invitation resent.', { duration: 4000 });
      await queryClient.invalidateQueries({ queryKey: ['access-members'] });
      await refetch();
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to resend invitation.', { duration: 6000 });
    },
  });

  const revokeMutation = useMutation({
    mutationFn: (email: string) => accessService.revoke(email),
    onSuccess: async () => {
      toast.success('User access has been revoked from the platform.', { duration: 4000 });
      setShowRevokeModal(false);
      setRevokeEmail(null);
      await queryClient.invalidateQueries({ queryKey: ['access-members'] });
      await refetch();
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to revoke user access.', { duration: 6000 });
    },
  });

  const sendSurveyMutation = useMutation({
    mutationFn: (email: string) => accessService.sendSurvey(email),
    onSuccess: async (res) => {
      toast.success(res.message || 'Survey sent.', { duration: 4000 });
      await queryClient.invalidateQueries({ queryKey: ['access-members'] });
      await refetch();
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to send survey.', { duration: 6000 });
    },
  });

  const importResponseMutation = useMutation({
    mutationFn: (data: { email: string; raw: Record<string, any>; submittedAt?: string; sheetRowId?: string }) =>
      accessService.importResponse(data),
    onSuccess: async () => {
      toast.success('Survey response imported successfully.', { duration: 4000 });
      setShowImportModal(false);
      setImportJson('');
      if (responsesEmail) {
        const res = await accessService.getResponses(responsesEmail);
        setResponses(res.responses || []);
      }
      await queryClient.invalidateQueries({ queryKey: ['access-members'] });
      await refetch();
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to import survey response.', { duration: 6000 });
    },
  });

  const membersTable = useMemo(() => members, [members]);

  const displayNameForEmail = (email: string) => {
    const local = (email || '').split('@')[0] || '';
    return local || '-';
  };

  const displayNameForMember = (m: AccessMemberRow) => displayNameForEmail(m.email);

  const openResponses = async (email: string) => {
    setResponsesEmail(email);
    setShowResponsesModal(true);
    try {
      const res = await accessService.getResponses(email);
      setResponses(res.responses || []);
    } catch (e: any) {
      setResponses([]);
      toast.error(e?.response?.data?.message || 'Failed to load responses.', { duration: 6000 });
    }
  };

  const formatDate = (v?: string | null) => {
    if (!v) return '-';
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return v;
    return d.toLocaleString();
  };

  const onInviteSubmit = () => {
    const email = emailInput.trim().toLowerCase();
    if (!email) {
      toast.error('Please enter an email.', { duration: 4000 });
      return;
    }
    inviteMutation.mutate(email);
  };

  const openRevokeConfirm = (email: string) => {
    setRevokeEmail(email);
    setShowRevokeModal(true);
  };

  const handleRevokeConfirm = () => {
    if (revokeEmail) {
      revokeMutation.mutate(revokeEmail);
    }
  };

  const approveRequestMutation = useMutation({
    mutationFn: (email: string) => accessService.approveRequest(email),
    onSuccess: async () => {
      toast.success('Access request approved. Invitation email sent.', { duration: 4000 });
      await queryClient.invalidateQueries({ queryKey: ['access-members'] });
      await queryClient.invalidateQueries({ queryKey: ['access-requests'] });
      await refetchRequests();
      await refetch();
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to approve request.', { duration: 6000 });
    },
  });

  const denyRequestMutation = useMutation({
    mutationFn: (email: string) => accessService.denyRequest(email),
    onSuccess: async () => {
      toast.success('Access request denied.', { duration: 4000 });
      await queryClient.invalidateQueries({ queryKey: ['access-members'] });
      await queryClient.invalidateQueries({ queryKey: ['access-requests'] });
      await refetchRequests();
      await refetch();
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to deny request.', { duration: 6000 });
    },
  });

  // Handle email prefilling from URL params
  useEffect(() => {
    const emailParam = searchParams.get('email');
    if (emailParam) {
      setEmailInput(emailParam);
      setActiveTab('requests');
      // Clear the email param from URL
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('email');
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">Access Control</h1>
            <p className="text-muted-foreground mt-1">
              Invite users to Our Digi Folio (closed access), view registered users, send surveys, and review responses.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              refetch();
              queryClient.invalidateQueries({ queryKey: ['access-members'] });
            }}
            disabled={isLoading}
          >
            <RotateCcw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2">
          <Button
            variant={activeTab === 'invited' ? 'default' : 'outline'}
            onClick={() => setActiveTab('invited')}
            className="relative"
          >
            Invitations
            {invitedMembers.length > 0 && (
              <span
                className={`ml-2 px-2 py-0.5 text-xs font-semibold rounded-full ${
                  activeTab === 'invited'
                    ? 'bg-white/30 text-white'
                    : 'bg-primary text-primary-foreground'
                }`}
              >
                {invitedMembers.length}
              </span>
            )}
          </Button>
          <Button
            variant={activeTab === 'registered' ? 'default' : 'outline'}
            onClick={() => setActiveTab('registered')}
            className="relative"
          >
            Registered
            {registeredMembers.length > 0 && (
              <span
                className={`ml-2 px-2 py-0.5 text-xs font-semibold rounded-full ${
                  activeTab === 'registered'
                    ? 'bg-white/30 text-white'
                    : 'bg-primary text-primary-foreground'
                }`}
              >
                {registeredMembers.length}
              </span>
            )}
          </Button>
          <Button
            variant={activeTab === 'requests' ? 'default' : 'outline'}
            onClick={() => setActiveTab('requests')}
            className="relative"
          >
            Access Requests
            {requests.length > 0 && (
              <span
                className={`ml-2 px-2 py-0.5 text-xs font-semibold rounded-full ${
                  activeTab === 'requests'
                    ? 'bg-white/30 text-white'
                    : 'bg-primary text-primary-foreground'
                }`}
              >
                {requests.length}
              </span>
            )}
          </Button>
        </div>

        {/* Invitations tab */}
        {activeTab === 'invited' && (
          <Card className="p-6 space-y-4">
            <div className="flex flex-col md:flex-row gap-3 md:items-end">
              <div className="flex-1">
                <label className="block text-sm font-medium mb-1">Invite email</label>
                <Input
                  placeholder="name@company.com"
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                />
              </div>
              <Button onClick={onInviteSubmit} disabled={inviteMutation.isPending}>
                <Mail className="h-4 w-4 mr-2" />
                {inviteMutation.isPending ? 'Inviting...' : 'Send invite'}
              </Button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left border-b">
                    <th className="py-2 pr-3">Email</th>
                    <th className="py-2 pr-3">User name</th>
                    <th className="py-2 pr-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td className="py-4 text-muted-foreground" colSpan={3}>
                        Loading invitations...
                      </td>
                    </tr>
                  ) : membersTable.length === 0 ? (
                    <tr>
                      <td className="py-4 text-muted-foreground" colSpan={3}>
                        No invited users yet.
                      </td>
                    </tr>
                  ) : (
                    membersTable.map((m: AccessMemberRow) => (
                      <tr key={m.id} className="border-b last:border-b-0">
                        <td className="py-2 pr-3 font-medium">{m.email}</td>
                        <td className="py-2 pr-3">{displayNameForMember(m)}</td>
                        <td className="py-2 pr-3">
                          <div className="flex flex-wrap gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => resendMutation.mutate(m.email)}
                              disabled={resendMutation.isPending}
                            >
                              <RefreshCcw className="h-4 w-4 mr-2" />
                              Resend
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openRevokeConfirm(m.email)}
                              disabled={true}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Remove
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {/* Registered tab */}
        {activeTab === 'registered' && (
          <Card className="p-6 space-y-4">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left border-b">
                    <th className="py-2 pr-3">Email</th>
                    <th className="py-2 pr-3">User name</th>
                    <th className="py-2 pr-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td className="py-4 text-muted-foreground" colSpan={3}>
                        Loading registered users...
                      </td>
                    </tr>
                  ) : membersTable.length === 0 ? (
                    <tr>
                      <td className="py-4 text-muted-foreground" colSpan={3}>
                        No registered users yet.
                      </td>
                    </tr>
                  ) : (
                    membersTable.map((m: AccessMemberRow) => (
                      <tr key={m.id} className="border-b last:border-b-0">
                        <td className="py-2 pr-3 font-medium">{m.email}</td>
                        <td className="py-2 pr-3">{displayNameForMember(m)}</td>
                        <td className="py-2 pr-3">
                          <div className="flex flex-wrap gap-2">
                            <Button
                              size="sm"
                              onClick={() => sendSurveyMutation.mutate(m.email)}
                              disabled={sendSurveyMutation.isPending}
                            >
                              <Send className="h-4 w-4 mr-2" />
                              Send survey
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => openResponses(m.email)}>
                              <Eye className="h-4 w-4 mr-2" />
                              View responses
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openRevokeConfirm(m.email)}
                              disabled={true}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Remove
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {/* Access Requests tab */}
        {activeTab === 'requests' && (
          <Card className="p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold">Pending Access Requests</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Review and approve or deny access requests from users.
                </p>
              </div>
            </div>

            {isLoadingRequests ? (
              <div className="text-center py-8 text-muted-foreground">Loading requests...</div>
            ) : requests.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">No pending access requests.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left border-b">
                      <th className="py-2 pr-3">Email</th>
                      <th className="py-2 pr-3">User name</th>
                      <th className="py-2 pr-3">Requested at</th>
                      <th className="py-2 pr-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {requests.map((req) => (
                      <tr key={req.id} className="border-b hover:bg-muted/50">
                        <td className="py-2 pr-3 font-medium">{req.email}</td>
                        <td className="py-2 pr-3">{displayNameForMember(req)}</td>
                        <td className="py-2 pr-3">{formatDate(req.invitedAt)}</td>
                        <td className="py-2 pr-3">
                          <div className="flex flex-wrap gap-2">
                            <Button
                              size="sm"
                              onClick={() => approveRequestMutation.mutate(req.email)}
                              disabled={approveRequestMutation.isPending || denyRequestMutation.isPending}
                              className="bg-green-600 hover:bg-green-700"
                            >
                              <CheckCircle2 className="h-4 w-4 mr-2" />
                              Approve
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => denyRequestMutation.mutate(req.email)}
                              disabled={approveRequestMutation.isPending || denyRequestMutation.isPending}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <XCircle className="h-4 w-4 mr-2" />
                              Deny
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        )}

        {/* Responses modal */}
        {showResponsesModal && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowResponsesModal(false)}
          >
            <Card className="w-full max-w-3xl max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-start justify-between gap-3 mb-4">
                <div>
                  <h2 className="text-lg font-semibold">Survey responses</h2>
                  <p className="text-sm text-muted-foreground">{responsesEmail}</p>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setShowImportModal(true);
                      setImportJson('');
                    }}
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Import Response
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setShowResponsesModal(false)}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {responses.length === 0 ? (
                <p className="text-sm text-muted-foreground">No responses found.</p>
              ) : (
                <div className="space-y-3">
                  {responses.map((r) => (
                    <Card key={r.id} className="p-4">
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                        <div className="text-sm">
                          <div>
                            <span className="font-medium">Imported:</span> {formatDate(r.importedAt)}
                          </div>
                          {r.submittedAt && (
                            <div>
                              <span className="font-medium">Submitted:</span> {formatDate(r.submittedAt)}
                            </div>
                          )}
                        </div>
                        {r.sheetRowId && (
                          <div className="text-xs text-muted-foreground">Sheet row: {r.sheetRowId}</div>
                        )}
                      </div>
                      <pre className="mt-3 text-xs bg-muted/40 p-3 rounded overflow-x-auto">
                        {JSON.stringify(r.raw, null, 2)}
                      </pre>
                    </Card>
                  ))}
                </div>
              )}
            </Card>
          </div>
        )}

        {/* Import Response Modal */}
        {showImportModal && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => {
              if (!importResponseMutation.isPending) {
                setShowImportModal(false);
                setImportJson('');
              }
            }}
          >
            <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-start justify-between gap-3 mb-4">
                <div>
                  <h2 className="text-lg font-semibold">Import Survey Response</h2>
                  <p className="text-sm text-muted-foreground">Paste the survey response JSON data</p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    if (!importResponseMutation.isPending) {
                      setShowImportModal(false);
                      setImportJson('');
                    }
                  }}
                  disabled={importResponseMutation.isPending}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Response JSON</label>
                  <textarea
                    className="w-full h-64 p-3 border rounded-md font-mono text-sm"
                    placeholder='{"email": "user@example.com", "name": "John Doe", "message": "..."}'
                    value={importJson}
                    onChange={(e) => setImportJson(e.target.value)}
                    disabled={importResponseMutation.isPending}
                  />
                  <p className="text-xs text-muted-foreground mt-2">
                    Paste the JSON data from Google Forms response. The email field is required.
                  </p>
                </div>

                <div className="flex gap-3 justify-end">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowImportModal(false);
                      setImportJson('');
                    }}
                    disabled={importResponseMutation.isPending}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => {
                      if (!responsesEmail || !importJson.trim()) {
                        toast.error('Email and JSON data are required.', { duration: 4000 });
                        return;
                      }

                      try {
                        const parsed = JSON.parse(importJson);
                        if (!parsed || typeof parsed !== 'object') {
                          toast.error('Invalid JSON format.', { duration: 4000 });
                          return;
                        }

                        importResponseMutation.mutate({
                          email: responsesEmail,
                          raw: parsed,
                          submittedAt: parsed.submittedAt || parsed.timestamp || new Date().toISOString(),
                          sheetRowId: parsed.sheetRowId || parsed.id || undefined,
                        });
                      } catch (error) {
                        toast.error('Invalid JSON format. Please check your data.', { duration: 6000 });
                      }
                    }}
                    disabled={importResponseMutation.isPending || !importJson.trim()}
                  >
                    {importResponseMutation.isPending ? 'Importing...' : 'Import Response'}
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Revoke confirmation modal */}
        {showRevokeModal && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => {
              if (!revokeMutation.isPending) {
                setShowRevokeModal(false);
                setRevokeEmail(null);
              }
            }}
          >
            <Card className="w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-start justify-between gap-3 mb-4">
                <div>
                  <h2 className="text-lg font-semibold text-red-600">Remove User from Platform</h2>
                  <p className="text-sm text-muted-foreground mt-1">This action cannot be undone.</p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    if (!revokeMutation.isPending) {
                      setShowRevokeModal(false);
                      setRevokeEmail(null);
                    }
                  }}
                  disabled={revokeMutation.isPending}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>

              <div className="space-y-4">
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-900 font-medium mb-2">⚠️ Warning</p>
                  <p className="text-sm text-red-800">
                    Are you sure you want to remove <strong>{revokeEmail}</strong> from the platform?
                  </p>
                  <ul className="text-sm text-red-700 mt-3 space-y-1 list-disc list-inside">
                    <li>User access will be permanently revoked</li>
                    <li>All pending invitations will be invalidated</li>
                    <li>The user will not be able to register or access the platform</li>
                    <li>This action cannot be undone</li>
                  </ul>
                </div>

                <div className="flex gap-3 justify-end">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowRevokeModal(false);
                      setRevokeEmail(null);
                    }}
                    disabled={revokeMutation.isPending}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={handleRevokeConfirm}
                    disabled={revokeMutation.isPending}
                  >
                    {revokeMutation.isPending ? 'Removing...' : 'Yes, Remove User'}
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        )}
      </div>
    </Layout>
  );
}

